import React, { useCallback } from 'react';

import orderBy from 'lodash.orderby';

import type { RAsyncItemsRendererProps } from '@/_components';
import {
  FormField,
  Label,
  RAsyncMultiCombobox,
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@/_components';
import { TableHeading } from '@/_containers';
import { useGetInventoryBinLocationMutation } from '@/_pages/settings/inventory-bin-locations/shared';
import type { InventoryBinLocationQuerySchema } from '@/_pages/settings/inventory-bin-locations/shared/schemas';

import { InventoryBinLocationItem } from './item';
import { NoInventoryBinLocations } from './no-bin-locations';

import { useInventoryFormContext } from '../../form-context';

type InventoryBinLocationsSectionProps = {
  disabled: boolean;
};

export function MultiBinLocationsSection({ disabled }: InventoryBinLocationsSectionProps) {
  const form = useInventoryFormContext();
  const [binLocations] = form.watch(['bin_locations']);

  const setBinLocationType = useCallback(
    (binLocation: InventoryBinLocationQuerySchema, isCurrentlyPrimary: boolean) => {
      const newBinLocations = binLocations?.map((v) => {
        let nVal = { ...v, pivot: { ...(v.pivot || {}), type: 'secondary' } };

        if (v.id === binLocation.id && !isCurrentlyPrimary) {
          nVal = {
            ...v,
            pivot: { ...(v.pivot || {}), type: 'primary' },
          };
        }
        return nVal as InventoryBinLocationQuerySchema;
      });

      form.setValue('bin_locations', newBinLocations, { shouldDirty: true });
    },
    [form, binLocations]
  );

  const getSortedValues = React.useCallback((values: InventoryBinLocationQuerySchema[]) => {
    const sortedAsc = orderBy(values, ['location']);
    const primaryLocations = sortedAsc.filter((v) => v.pivot?.type === 'primary');
    const secondaryLocations = sortedAsc.filter((v) => v.pivot?.type !== 'primary');
    return [...primaryLocations, ...secondaryLocations];
  }, []);

  const renderBinLocationsList: RAsyncItemsRendererProps<InventoryBinLocationQuerySchema> = React.useCallback(
    ({ values, removeItem }) => {
      if (values.length === 0) {
        return <NoInventoryBinLocations />;
      }
      const sortedValues = getSortedValues(values);
      return (
        <section className="col-span-12 flex flex-col gap-4">
          {disabled && <Label>Bin Locations</Label>}
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent dark:hover:bg-transparent">
                <TableHeading title="Location" />
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedValues.map((item) => (
                <InventoryBinLocationItem
                  key={item.id}
                  binLocation={item}
                  isEditing={!disabled}
                  onDelete={removeItem}
                  setBinLocationType={setBinLocationType}
                />
              ))}
            </TableBody>
          </Table>
          {/* </div> */}
        </section>
      );
    },
    [disabled, setBinLocationType, getSortedValues]
  );

  const filterBinLocations = React.useCallback(
    (item: InventoryBinLocationQuerySchema) => !!(binLocations || []).find(({ id }) => id === item.id),
    [binLocations]
  );

  const itemToString = React.useCallback((item: InventoryBinLocationQuerySchema) => item.location, []);

  const keyExtractor = React.useCallback((item: InventoryBinLocationQuerySchema) => item.id.toString(), []);

  return (
    <div className="col-span-12 mt-6 flex flex-col gap-2">
      <FormField
        control={form.control}
        name="bin_locations"
        render={({ field }) => (
          <RAsyncMultiCombobox<InventoryBinLocationQuerySchema>
            label="Bin Locations"
            options={binLocations || []}
            value={
              binLocations && binLocations.length > 0
                ? binLocations[0]
                : ({ id: -1 } as InventoryBinLocationQuerySchema)
            }
            hideClearIndicator
            onValueChange={field.onChange}
            isEditable={!disabled}
            filterOptions={filterBinLocations}
            renderItems={renderBinLocationsList}
            keyExtractor={keyExtractor}
            itemToString={itemToString}
            sortValues={getSortedValues}
            useApi={useGetInventoryBinLocationMutation}
          />
        )}
      />
    </div>
  );
}
